import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Wand2, FolderOpen, LayoutGrid, Users, MapPin, Images, Sparkles, Music,
  Download, Zap, Settings, LogOut, Bell, Bot, Menu, ShieldCheck, Send, Clapperboard, GitCompare,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import StudioTutorial from "./StudioTutorial";

const NAV = [
  ["", Home, "studio.home"],
  ["quick", Zap, "studio.quick"],
  ["create", Wand2, "studio.create"],
  ["projects", FolderOpen, "studio.projects"],
  ["storyboards", LayoutGrid, "studio.storyboards"],
  ["variations", GitCompare, "studio.variations"],
  ["characters", Users, "studio.characters"],
  ["locations", MapPin, "studio.locations"],
  ["assets", Images, "studio.assets"],
  ["templates", Sparkles, "studio.templates"],
  ["audio", Music, "studio.audio"],
  ["exports", Download, "studio.exports"],
  ["credits", Zap, "studio.credits"],
  ["settings", Settings, "studio.settings"],
];

function Copilot({ open, onClose }) {
  const { locale } = useI18n();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const loc = useLocation();
  const projectId = loc.pathname.match(/\/studio\/projects\/([^/]+)/)?.[1];

  const send = async () => {
    if (!input.trim()) return;
    const text = input;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/copilot", {
        message: text,
        project_id: projectId,
        history: msgs.map((m) => ({ role: m.role, text: m.text })),
      });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, actions: data.actions || [] }]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async (actions) => {
    try {
      const { data } = await api.post("/ai/copilot/apply", { actions });
      toast.success(
        data.applied.length
          ? `${data.applied.length} ${locale === "fr" ? "action(s) appliquée(s)" : "action(s) applied"}`
          : locale === "fr" ? "Rien à appliquer" : "Nothing to apply"
      );
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!open) return null;
  return (
    <aside
      data-testid="copilot-panel"
      className="glass fixed bottom-0 right-0 top-14 z-40 flex w-full max-w-[400px] flex-col border-l border-white/[0.08]"
    >
      <div className="flex items-center justify-between border-b border-[#212230] px-5 py-4">
        <p className="flex items-center gap-2 font-display text-sm font-semibold">
          <Bot className="h-4 w-4 text-[#00d5ff]" /> Movies Copilot
        </p>
        <button onClick={onClose} data-testid="copilot-close" className="text-[#9194a6] hover:text-white">
          ✕
        </button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        {msgs.length === 0 && (
          <div className="space-y-2">
            {(locale === "fr"
              ? ["Rends la scène plus sombre et ajoute de la pluie",
                 "Crée trois fins alternatives",
                 "Fais un travelling arrière sur le plan 3"]
              : ["Make the scene darker and add rain",
                 "Create three alternative endings",
                 "Give shot 3 a slow pull-out"]
            ).map((s) => (
              <button
                key={s}
                onClick={() => setInput(s)}
                className="block w-full rounded-lg border border-[#212230] bg-[#0e0e15] p-3 text-left text-[12.5px] text-[#9194a6] transition-colors hover:border-[#383b4d] hover:text-white"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : ""}>
            <div
              className={`inline-block max-w-[92%] rounded-xl px-4 py-2.5 text-[13px] leading-relaxed ${
                m.role === "user" ? "bg-[#ff4b2b] text-white" : "border border-[#212230] bg-[#0e0e15] text-[#d3d5df]"
              }`}
            >
              {m.text}
            </div>
            {m.actions?.length > 0 && (
              <button
                onClick={() => apply(m.actions)}
                data-testid="copilot-apply"
                className="btn-ghost mt-2 block rounded-full px-4 py-1.5 text-[11px] font-semibold"
              >
                {locale === "fr" ? "Appliquer" : "Apply"} ({m.actions.length})
              </button>
            )}
          </div>
        ))}
        {busy && <p className="mono text-[11px] uppercase tracking-[0.2em] text-[#00d5ff]">thinking…</p>}
      </div>
      <div className="flex gap-2 border-t border-[#212230] p-4">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="copilot-input"
          placeholder={locale === "fr" ? "Que voulez-vous changer ?" : "What do you want to change?"}
          className="flex-1 rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[13px] outline-none focus:border-[#ff4b2b]/60"
        />
        <button onClick={send} data-testid="copilot-send" className="btn-amber rounded-lg px-3">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}

export default function StudioLayout() {
  const { user, loading, logout, refresh } = useAuth();
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [copilot, setCopilot] = useState(false);
  const [sidebar, setSidebar] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [bell, setBell] = useState(false);

  useEffect(() => {
    if (user === false) nav(`/${locale}/login`, { replace: true });
  }, [user, locale, nav]);

  const uid = user?.user_id || user?.id || null;

  useEffect(() => {
    if (!uid) return;
    const load = () => {
      api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
      refresh();
    };
    load();
    const i = setInterval(load, 120000);
    return () => clearInterval(i);
  }, [uid, refresh]);

  if (loading || !user)
    return (
      <div className="grid min-h-screen place-items-center">
        <p className="mono text-[11px] uppercase tracking-[0.24em] text-[#9194a6]">Loading studio…</p>
      </div>
    );

  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="studio-shell">
      <header className="glass fixed inset-x-0 top-0 z-40 flex h-14 max-w-full items-center justify-between gap-2 border-b border-white/[0.08] px-3 sm:px-4 lg:px-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <button onClick={() => setSidebar(!sidebar)} className="shrink-0 lg:hidden" data-testid="studio-sidebar-toggle">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/studio" aria-label="Studio Movies IA" className="flex min-w-0 items-center gap-2.5">
            <span aria-hidden="true" className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#ff4b2b]">
              <Clapperboard className="h-3.5 w-3.5" strokeWidth={2.4} />
            </span>
            <span className="hidden truncate font-display text-[13px] font-semibold tracking-tight sm:inline">STUDIO MOVIES IA</span>
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <Link
            to="/studio/credits"
            data-testid="credit-meter"
            className="mono flex shrink-0 items-center gap-1.5 rounded-full border border-[#2a2c3c] bg-[#ff4b2b]/[0.07] px-2.5 py-1.5 text-[11px] text-[#ffc76b] transition-all hover:border-[#ff4b2b]/60 hover:shadow-[0_0_22px_-6px_rgba(255,75,43,0.7)] sm:px-3"
          >
            <Zap className="h-3 w-3 text-[#ff4b2b]" />
            {Number(user.credits).toLocaleString()}
          </Link>
          <StudioTutorial />
          <button onClick={() => setCopilot(!copilot)} data-testid="copilot-toggle" className="btn-ghost hidden rounded-full px-3 py-1.5 text-[11px] font-semibold sm:inline-flex">
            <Bot className="h-3.5 w-3.5" />
          </button>
          <div className="relative shrink-0">
            <button onClick={() => { setBell(!bell); if (unread) api.post("/notifications/read").then(() => setNotifs((n) => n.map((x) => ({ ...x, read: true })))); }} data-testid="notif-bell" className="relative grid h-8 w-8 place-items-center rounded-full border border-[#2a2c3c]">
              <Bell className="h-3.5 w-3.5" />
              {unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-[#ff4b2b] text-[9px] font-bold">{unread}</span>}
            </button>
            {bell && (
              <div className="absolute right-0 top-11 z-50 w-[78vw] max-w-[300px] rounded-xl border border-[#212230] bg-[#0a0a0e] p-3 sm:w-[300px]" data-testid="notif-list">
                {notifs.length === 0 && <p className="p-3 text-[12.5px] text-[#9194a6]">{locale === "fr" ? "Aucune notification" : "No notifications"}</p>}
                {notifs.slice(0, 8).map((n) => (
                  <div key={n.id} className="border-b border-[#1a1c24] p-3 last:border-0">
                    <p className="text-[12.5px] font-medium">{n.title}</p>
                    <p className="mt-0.5 text-[11.5px] text-[#9194a6]">{n.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {user.role === "admin" && (
            <Link to="/admin" data-testid="goto-admin" className="btn-ghost hidden rounded-full px-3 py-1.5 text-[11px] font-semibold sm:flex">
              <ShieldCheck className="h-3.5 w-3.5" />
            </Link>
          )}
          <button onClick={async () => { await logout(); nav(`/${locale}`); }} data-testid="logout-btn" className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#2a2c3c] text-[#9194a6] transition-colors hover:text-white">
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <aside
        className={`glass fixed bottom-0 left-0 top-14 z-30 w-[220px] overflow-y-auto border-r border-white/[0.08] px-3 py-5 transition-transform duration-300 lg:translate-x-0 ${
          sidebar ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="space-y-0.5">
          {NAV.map(([path, Icon, key]) => (
            <NavLink
              key={path}
              to={`/studio/${path}`}
              end={path === ""}
              onClick={() => setSidebar(false)}
              data-testid={`studio-nav-${path || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] transition-colors duration-200 ${
                  isActive ? "bg-white/[0.07] text-white" : "text-[#9194a6] hover:bg-white/[0.04] hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => { setCopilot(true); setSidebar(false); }}
          data-testid="studio-nav-copilot"
          className="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] text-[#9194a6] transition-colors hover:bg-white/[0.04] hover:text-white sm:hidden"
        >
          <Bot className="h-4 w-4" /> Movies Copilot
        </button>
        <div className="mt-8 rounded-xl border border-[#212230] bg-[#0e0e15] p-4">
          <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#8288a0]">Plan</p>
          <p className="mt-1.5 font-display text-sm font-semibold capitalize">{user.plan}</p>
          <Link to={`/${locale}/pricing`} data-testid="sidebar-upgrade" className="btn-amber mt-4 block rounded-full py-2 text-center text-[11px] font-semibold">
            {locale === "fr" ? "Améliorer" : "Upgrade"}
          </Link>
        </div>
      </aside>

      <main className={`overflow-x-hidden pt-14 lg:pl-[220px] ${copilot ? "lg:pr-[400px]" : ""}`}>
        <Outlet />
      </main>

      <Copilot open={copilot} onClose={() => setCopilot(false)} />
    </div>
  );
}
